import { testnetAddresses as chainTestnetAddresses } from '@endure/chains';
import { type Address, getAddress, isAddress } from 'viem';

import deployAddresses from '../../../deploy/addresses.testnet.json';

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

// Keep in lockstep with `addresses.testnet.json` and `@endure/chains.testnetAddresses`.
// Any address present in the deploy artifact AND in the chains-package mirror MUST be
// listed here so a stale entry trips the drift check at config-init. The `chainId` key
// in addresses.testnet.json is metadata (not an address) and is intentionally excluded.
export const requiredTestnetAddressKeys = [
  'unitroller',
  'diamond',
  'comptrollerLens',
  'accessControlManager',
  'acmCommandsAggregator',
  'resilientOracle',
  'marketFacet',
  'policyFacet',
  'setterFacet',
  'rewardFacet',
  'flashLoanFacet',
  'vWTAO',
  'wsTAO',
  'wsTAOPriceFeed',
  'vWsTAO',
  'vAlpha30',
  'vAlpha64',
  'irmWTAO',
  'irmAlpha',
  'wtao',
  'mockAlpha30',
  'mockAlpha64',
  'bittensorNativeAlphaFeed',
  'wAlphaStashFaucet',
  'wAlpha288',
  'wAlpha333',
  'wAlpha418',
  'wAlphaPriceFeed288',
  'wAlphaPriceFeed333',
  'wAlphaPriceFeed418',
  'vWAlpha288',
  'vWAlpha333',
  'vWAlpha418',
  'wAlpha288V2',
  'wAlpha333V2',
  'wAlpha418V2',
  'wAlphaPriceFeed288V2',
  'wAlphaPriceFeed333V2',
  'wAlphaPriceFeed418V2',
  'vWAlpha288V2',
  'vWAlpha333V2',
  'vWAlpha418V2',
] as const;

export type TestnetAddressKey = (typeof requiredTestnetAddressKeys)[number];

export type TestnetAddresses = Record<TestnetAddressKey, Address>;

const parseTestnetAddress = (key: TestnetAddressKey): Address => {
  const address = (deployAddresses as Record<string, string | number>)[key];

  if (typeof address !== 'string' || !isAddress(address) || getAddress(address) === ZERO_ADDRESS) {
    throw new Error(`Invalid Bittensor testnet deploy address for ${key}`);
  }

  return getAddress(address);
};

export const getTestnetDeployAddresses = () =>
  requiredTestnetAddressKeys.reduce<TestnetAddresses>((acc, key) => {
    acc[key] = parseTestnetAddress(key);

    return acc;
  }, {} as TestnetAddresses);

export const assertTestnetDeployAddressesAreUsable = () => {
  const testnetDeployAddresses = getTestnetDeployAddresses();
  const staleAddressKeys = requiredTestnetAddressKeys.filter((key) => {
    const chainAddress = (chainTestnetAddresses as Record<TestnetAddressKey, Address>)[key];

    return getAddress(chainAddress) !== testnetDeployAddresses[key];
  });

  if (staleAddressKeys.length > 0) {
    throw new Error(
      `Bittensor testnet address metadata is stale for: ${staleAddressKeys.join(', ')}. Refresh @endure/chains testnetAddresses from packages/deploy/addresses.testnet.json.`,
    );
  }
};
